import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ChevronLeft, ChevronRight, Info, ShieldCheck, Upload, Building2,
  Landmark, CreditCard, FileSignature, Check, AlertCircle, HelpCircle, ChevronDown, ChevronUp, Loader2
} from "lucide-react";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/onboarding`;

const STEPS = [
  { id: 1, title: "Company Identity", subtitle: "Legal & compliance info", icon: Building2 },
  { id: 2, title: "Financial Profile", subtitle: "Bank, revenue & funding", icon: Landmark },
  { id: 3, title: "Credit Bureau", subtitle: "Bureau authorization", icon: ShieldCheck },
  { id: 4, title: "Card Setup", subtitle: "Program configuration", icon: CreditCard },
  { id: 5, title: "Authorization", subtitle: "Review & sign", icon: FileSignature },
];

function VerifiedBadge() {
  return <Badge className="bg-green-50 text-green-600 border-green-200 text-[9px] h-5 font-medium ml-2" data-testid="verified-badge"><ShieldCheck className="h-3 w-3 mr-0.5" />Verified from Rippling</Badge>;
}

function WhyBox({ children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mt-3 border border-[#E2E8F0] rounded-lg overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 text-xs text-[#64748B] hover:bg-gray-100 transition-colors" data-testid="why-toggle">
        <span className="flex items-center gap-1.5"><HelpCircle className="h-3.5 w-3.5" />Why do we ask for this?</span>
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && <div className="px-3 py-2.5 text-xs text-[#64748B] leading-relaxed bg-white">{children}</div>}
    </div>
  );
}

function FormField({ label, required, verified, children, hint }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center">
        <Label className="text-xs font-medium text-[#502D3C]">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>
        {verified && <VerifiedBadge />}
      </div>
      {children}
      {hint && <p className="text-[10px] text-[#94A3B8]">{hint}</p>}
    </div>
  );
}

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeId = searchParams.get("resume");

  const [step, setStep] = useState(1);
  const [appId, setAppId] = useState(resumeId || null);
  const [isHris, setIsHris] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refData, setRefData] = useState(null);
  const [demoEins, setDemoEins] = useState([]);
  const [hrisLookupEin, setHrisLookupEin] = useState("");
  const [hrisLoading, setHrisLoading] = useState(false);
  const [plaidConnecting, setPlaidConnecting] = useState(false);
  const [plaidConnected, setPlaidConnected] = useState(false);
  const [bankMetrics, setBankMetrics] = useState(null);
  const [idUploaded, setIdUploaded] = useState(false);

  // Form state
  const [s1a, setS1a] = useState({ legal_name: "", dba_name: "", ein: "", state_of_incorporation: "", entity_type: "", date_of_incorporation: "", street: "", city: "", state: "", zip: "" });
  const [s1b, setS1b] = useState({ full_name: "", title: "", dob: "", address_street: "", address_city: "", address_state: "", address_zip: "", ssn: "" });
  const [s2a, setS2a] = useState({ bank_name: "", account_type: "", routing_number: "", account_last4: "" });
  const [s2b, setS2b] = useState({ annual_revenue: "", mrr: "", revenue_model: [], profitable: "", employee_count: "", monthly_payroll: "" });
  const [s2c, setS2c] = useState({ total_funding_raised: "", latest_round_stage: "", latest_round_amount: "", latest_round_date: "", lead_investors: "", other_investors: "", post_money_valuation: "" });
  const [s2d, setS2d] = useState({ existing_credit: "", outstanding_loans: "", defaults_history: "no" });
  const [s3, setS3] = useState({ duns_number: "", experian_bin: "", bureau_consent: false });
  const [s4, setS4] = useState({ estimated_monthly_spend: "", num_cards: "", spend_categories: [], billing_cycle: "", requested_limit: "", admin_name: "", admin_email: "", admin_phone: "" });
  const [s5, setS5] = useState({ cert_true: false, cert_bureau: false, cert_ach: false, cert_agreement: false, cert_officer: false, cert_monitoring: false, signature_name: "" });

  // Load reference data + demo EINs
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/reference-data`),
      axios.get(`${API}/hris-demo-eins`),
    ]).then(([ref, eins]) => {
      setRefData(ref.data);
      setDemoEins(eins.data);
    }).catch(console.error);
  }, []);

  // Resume draft
  useEffect(() => {
    if (resumeId) {
      axios.get(`${API}/applications/${resumeId}`).then(r => {
        const d = r.data;
        if (d.section1a) setS1a(d.section1a);
        if (d.section1b) setS1b(d.section1b);
        if (d.section2a) setS2a(d.section2a);
        if (d.section2b) setS2b(d.section2b);
        if (d.section2c) setS2c(d.section2c);
        if (d.section2d) setS2d(d.section2d);
        if (d.section3) setS3(d.section3);
        if (d.section4) setS4(d.section4);
        if (d.is_hris) setIsHris(true);
        setStep(d.current_step || 1);
      }).catch(console.error);
    }
  }, [resumeId]);

  const saveDraft = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        id: appId, is_hris: isHris, current_step: step,
        section1a: s1a, section1b: s1b, section2a: { ...s2a, avg_daily_balance: bankMetrics?.avg_daily_balance },
        section2b: { ...s2b, annual_revenue: parseFloat(s2b.annual_revenue) || 0, employee_count: parseInt(s2b.employee_count) || 0 },
        section2c: { ...s2c, total_funding_raised: parseFloat(s2c.total_funding_raised) || 0, latest_round_amount: parseFloat(s2c.latest_round_amount) || 0 },
        section2d: s2d, section3: s3, section4: s4,
      };
      const res = await axios.post(`${API}/applications`, payload);
      if (!appId) setAppId(res.data.id);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  }, [appId, isHris, step, s1a, s1b, s2a, s2b, s2c, s2d, s3, s4, bankMetrics]);

  const handleHrisLookup = async () => {
    if (!hrisLookupEin) return;
    setHrisLoading(true);
    try {
      const r = await axios.get(`${API}/hris-lookup?ein=${hrisLookupEin}`);
      if (r.data.found) {
        const d = r.data.data;
        setIsHris(true);
        setS1a({ legal_name: d.legal_name, dba_name: d.dba_name || "", ein: d.ein, state_of_incorporation: d.state_of_incorporation, entity_type: d.entity_type, date_of_incorporation: d.date_of_incorporation, street: d.business_address.street, city: d.business_address.city, state: d.business_address.state, zip: d.business_address.zip });
        if (d.control_person) {
          setS1b(prev => ({ ...prev, full_name: d.control_person.full_name, title: d.control_person.title, dob: d.control_person.dob, address_street: d.control_person.address.street, address_city: d.control_person.address.city, address_state: d.control_person.address.state, address_zip: d.control_person.address.zip }));
        }
        if (d.bank_account) {
          setS2a({ bank_name: d.bank_account.bank_name, account_type: d.bank_account.account_type, routing_number: d.bank_account.routing_number, account_last4: d.bank_account.account_last4 });
          setPlaidConnected(true);
        }
        if (d.employee_count) setS2b(prev => ({ ...prev, employee_count: String(d.employee_count), monthly_payroll: String(d.monthly_payroll || "") }));
      }
    } catch (e) { console.error(e); }
    finally { setHrisLoading(false); }
  };

  const simulatePlaid = async () => {
    setPlaidConnecting(true);
    try {
      await new Promise(r => setTimeout(r, 2000));
      const r = await axios.post(`${API}/plaid-simulate`, { company: s1a.legal_name });
      setS2a({ bank_name: r.data.bank_name, account_type: r.data.account_type, routing_number: r.data.routing_number, account_last4: r.data.account_last4 });
      setBankMetrics(r.data);
      setPlaidConnected(true);
    } catch (e) { console.error(e); }
    finally { setPlaidConnecting(false); }
  };

  const handleSubmit = async () => {
    await saveDraft();
    const id = appId;
    if (!id) return;
    try {
      await axios.post(`${API}/applications/${id}/submit`);
      navigate(`/onboarding/status/${id}`);
    } catch (e) { console.error(e); }
  };

  const nextStep = async () => {
    await saveDraft();
    setStep(s => Math.min(s + 1, 5));
  };
  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  const totalFields = 35;
  const filledFields = [s1a.legal_name, s1a.ein, s1a.state_of_incorporation, s1a.entity_type, s1a.date_of_incorporation, s1a.street, s1a.city, s1a.state, s1a.zip, s1b.full_name, s1b.title, s1b.dob, s2a.bank_name, s2b.annual_revenue, s2b.employee_count, s2c.total_funding_raised, s2c.latest_round_stage, s3.bureau_consent, s4.estimated_monthly_spend, s4.num_cards, s4.billing_cycle, s4.admin_name, s4.admin_email].filter(Boolean).length;
  const progressPct = Math.round((filledFields / totalFields) * 100);

  if (!refData) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#502D3C]" /></div>;

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-[#F8FAFC]" data-testid="onboarding-wizard">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-white border-b border-[#E2E8F0] px-6 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" className="h-8 text-xs text-[#64748B]" onClick={() => navigate("/onboarding")} data-testid="back-to-list"><ChevronLeft className="h-3.5 w-3.5 mr-0.5" />Back</Button>
              <Separator orientation="vertical" className="h-5" />
              <div>
                <h1 className="text-base font-semibold text-[#502D3C]">Corporate Card Application</h1>
                <p className="text-[10px] text-[#64748B]">{isHris && <Badge className="bg-green-50 text-green-600 border-green-200 text-[8px] h-4 mr-1.5">HRIS Customer</Badge>}Step {step} of 5 — {STEPS[step - 1].title}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-[#94A3B8]">{progressPct}% complete</span>
              <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-[#502D3C] rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} /></div>
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={saveDraft} disabled={saving} data-testid="save-draft-btn">{saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}{saving ? "Saving..." : "Save Draft"}</Button>
            </div>
          </div>
          {/* Step indicators */}
          <div className="flex items-center gap-1" data-testid="step-indicators">
            {STEPS.map((s) => {
              const Icon = s.icon;
              const active = s.id === step;
              const done = s.id < step;
              return (
                <button key={s.id} onClick={() => s.id <= step && setStep(s.id)} className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] transition-all", active ? "bg-[#502D3C] text-white" : done ? "bg-green-50 text-green-700 hover:bg-green-100" : "bg-gray-50 text-[#94A3B8]")} data-testid={`step-${s.id}`}>
                  {done ? <Check className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
                  <span className="hidden sm:inline font-medium">{s.title}</span>
                </button>
              );
            })}
          </div>
        </header>

        <main className="max-w-4xl mx-auto p-6">
          {/* HRIS lookup banner (Step 1 only, if not already loaded) */}
          {step === 1 && !isHris && (
            <Card className="mb-6 border-[#FFCC1C]/50 bg-[#FFFDF0]" data-testid="hris-lookup-card">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#FFCC1C] rounded-lg flex items-center justify-center flex-shrink-0"><Building2 className="h-4 w-4 text-[#502D3C]" /></div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-[#502D3C]">Already on Rippling?</h3>
                    <p className="text-xs text-[#64748B] mt-0.5 mb-3">Enter your EIN to auto-fill from your HRIS account. This saves ~60% of the form.</p>
                    <div className="flex items-center gap-2">
                      <Input placeholder="XX-XXXXXXX" value={hrisLookupEin} onChange={e => setHrisLookupEin(e.target.value)} className="h-8 text-xs w-40 font-mono" data-testid="hris-ein-input" />
                      <Button size="sm" variant="outline" className="h-8 text-xs text-[#502D3C] border-[#502D3C]/30 hover:bg-[#502D3C]/5" onClick={handleHrisLookup} disabled={hrisLoading} data-testid="hris-lookup-btn">
                        {hrisLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}{hrisLoading ? "Looking up..." : "Auto-Fill"}
                      </Button>
                    </div>
                    {demoEins.length > 0 && (
                      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                        <span className="text-[9px] text-[#94A3B8]">Demo EINs:</span>
                        {demoEins.map(d => (
                          <button key={d.ein} onClick={() => { setHrisLookupEin(d.ein); }} className="text-[9px] text-[#502D3C] bg-white border border-[#E2E8F0] rounded px-1.5 py-0.5 hover:bg-gray-50 font-mono" data-testid={`demo-ein-${d.ein}`}>{d.ein} ({d.name})</button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* === STEP 1: Company Identity === */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-1-content">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#502D3C]">1A. Basic Company Information</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Legal Entity Name" required verified={isHris && !!s1a.legal_name}>
                      <Input value={s1a.legal_name} onChange={e => setS1a(p => ({ ...p, legal_name: e.target.value }))} placeholder="e.g. ABC Company Inc." className="h-9 text-sm" data-testid="field-legal-name" />
                    </FormField>
                    <FormField label="DBA / Trade Name" verified={isHris && !!s1a.dba_name}>
                      <Input value={s1a.dba_name} onChange={e => setS1a(p => ({ ...p, dba_name: e.target.value }))} placeholder="Operating name if different" className="h-9 text-sm" data-testid="field-dba" />
                    </FormField>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <FormField label="EIN (Tax ID)" required verified={isHris && !!s1a.ein} hint="Format: XX-XXXXXXX">
                      <Input value={s1a.ein} onChange={e => setS1a(p => ({ ...p, ein: e.target.value }))} placeholder="XX-XXXXXXX" className="h-9 text-sm font-mono" data-testid="field-ein" />
                    </FormField>
                    <FormField label="State of Incorporation" required verified={isHris && !!s1a.state_of_incorporation}>
                      <Select value={s1a.state_of_incorporation} onValueChange={v => setS1a(p => ({ ...p, state_of_incorporation: v }))}>
                        <SelectTrigger className="h-9 text-sm" data-testid="field-state-inc"><SelectValue placeholder="Select state" /></SelectTrigger>
                        <SelectContent>{refData.states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </FormField>
                    <FormField label="Entity Type" required verified={isHris && !!s1a.entity_type}>
                      <Select value={s1a.entity_type} onValueChange={v => setS1a(p => ({ ...p, entity_type: v }))}>
                        <SelectTrigger className="h-9 text-sm" data-testid="field-entity-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>{refData.entity_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </FormField>
                  </div>
                  <FormField label="Date of Incorporation" required verified={isHris && !!s1a.date_of_incorporation}>
                    <Input type="date" value={s1a.date_of_incorporation} onChange={e => setS1a(p => ({ ...p, date_of_incorporation: e.target.value }))} className="h-9 text-sm w-48" data-testid="field-date-inc" />
                  </FormField>
                  <Separator />
                  <p className="text-xs font-medium text-[#502D3C]">Registered Business Address{isHris && s1a.street && <VerifiedBadge />}</p>
                  <div className="grid grid-cols-1 gap-3">
                    <Input value={s1a.street} onChange={e => setS1a(p => ({ ...p, street: e.target.value }))} placeholder="Street address" className="h-9 text-sm" data-testid="field-street" />
                    <div className="grid grid-cols-3 gap-3">
                      <Input value={s1a.city} onChange={e => setS1a(p => ({ ...p, city: e.target.value }))} placeholder="City" className="h-9 text-sm" data-testid="field-city" />
                      <Select value={s1a.state} onValueChange={v => setS1a(p => ({ ...p, state: v }))}>
                        <SelectTrigger className="h-9 text-sm" data-testid="field-addr-state"><SelectValue placeholder="State" /></SelectTrigger>
                        <SelectContent>{refData.states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input value={s1a.zip} onChange={e => setS1a(p => ({ ...p, zip: e.target.value }))} placeholder="ZIP" className="h-9 text-sm" data-testid="field-zip" />
                    </div>
                  </div>
                  <WhyBox>Your EIN is used to pull your D&B business credit report, which tells us how promptly you pay other vendors. The registered address must match IRS records and is required for OFAC sanctions screening. Date of incorporation feeds into the credit model — older companies have lower baseline default rates.</WhyBox>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#502D3C]">1B. Beneficial Ownership</CardTitle>
                  <p className="text-[10px] text-[#64748B]">Required by the Corporate Transparency Act and FinCEN's CDD Rule</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                    <p className="text-xs text-blue-700 leading-relaxed">If your company has institutional investors (VC funds) and no individual owns 25%+, you only need to list one control person (e.g., CEO).</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Control Person Full Legal Name" required verified={isHris && !!s1b.full_name}>
                      <Input value={s1b.full_name} onChange={e => setS1b(p => ({ ...p, full_name: e.target.value }))} placeholder="Full legal name" className="h-9 text-sm" data-testid="field-cp-name" />
                    </FormField>
                    <FormField label="Title / Role" required verified={isHris && !!s1b.title}>
                      <Input value={s1b.title} onChange={e => setS1b(p => ({ ...p, title: e.target.value }))} placeholder="e.g. CEO & Co-Founder" className="h-9 text-sm" data-testid="field-cp-title" />
                    </FormField>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Date of Birth" required verified={isHris && !!s1b.dob} hint="Used for identity verification only">
                      <Input type="date" value={s1b.dob} onChange={e => setS1b(p => ({ ...p, dob: e.target.value }))} className="h-9 text-sm" data-testid="field-cp-dob" />
                    </FormField>
                    <FormField label="SSN or ITIN" required hint="XXX-XX-XXXX">
                      <Input value={s1b.ssn} onChange={e => setS1b(p => ({ ...p, ssn: e.target.value }))} placeholder="XXX-XX-XXXX" type="password" className="h-9 text-sm font-mono" data-testid="field-cp-ssn" />
                    </FormField>
                  </div>
                  {/* SSN callout */}
                  <div className="bg-[#502D3C]/5 border border-[#502D3C]/10 rounded-lg p-3" data-testid="ssn-callout">
                    <div className="flex items-start gap-2">
                      <ShieldCheck className="h-4 w-4 text-[#502D3C] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-[#502D3C]">We do NOT run personal credit checks.</p>
                        <p className="text-[11px] text-[#64748B] mt-0.5">Your SSN is used only for federally required identity verification (OFAC/sanctions screening) and will not affect your credit score. This is a key differentiator vs. traditional business cards.</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs font-medium text-[#502D3C] mt-2">Residential Address{isHris && s1b.address_street && <VerifiedBadge />}</p>
                  <div className="grid grid-cols-1 gap-3">
                    <Input value={s1b.address_street} onChange={e => setS1b(p => ({ ...p, address_street: e.target.value }))} placeholder="Street" className="h-9 text-sm" data-testid="field-cp-street" />
                    <div className="grid grid-cols-3 gap-3">
                      <Input value={s1b.address_city} onChange={e => setS1b(p => ({ ...p, address_city: e.target.value }))} placeholder="City" className="h-9 text-sm" data-testid="field-cp-city" />
                      <Select value={s1b.address_state} onValueChange={v => setS1b(p => ({ ...p, address_state: v }))}>
                        <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="State" /></SelectTrigger>
                        <SelectContent>{refData.states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input value={s1b.address_zip} onChange={e => setS1b(p => ({ ...p, address_zip: e.target.value }))} placeholder="ZIP" className="h-9 text-sm" data-testid="field-cp-zip" />
                    </div>
                  </div>
                  <FormField label="Government-Issued ID" required hint="Upload JPG, PNG, or PDF">
                    <div className="flex items-center gap-3">
                      <label className={cn("flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed cursor-pointer transition-colors", idUploaded ? "border-green-300 bg-green-50" : "border-gray-200 hover:border-[#502D3C]/30")} data-testid="field-id-upload">
                        {idUploaded ? <Check className="h-4 w-4 text-green-600" /> : <Upload className="h-4 w-4 text-[#94A3B8]" />}
                        <span className="text-xs text-[#64748B]">{idUploaded ? "ID uploaded" : "Choose file"}</span>
                        <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.pdf" onChange={() => setIdUploaded(true)} />
                      </label>
                    </div>
                  </FormField>
                  <WhyBox>Beneficial ownership information is a federal requirement under the Corporate Transparency Act. We screen all control persons against OFAC/sanctions lists. Government ID provides visual identity confirmation per Celtic Bank's KYC requirements.</WhyBox>
                </CardContent>
              </Card>
            </div>
          )}

          {/* === STEP 2: Financial Profile === */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-2-content">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#502D3C]">2A. Bank Account Verification</CardTitle>
                  <p className="text-[10px] text-[#64748B]">Connect your primary business bank account for ACH auto-debit and underwriting</p></CardHeader>
                <CardContent className="space-y-4">
                  {!plaidConnected ? (
                    <div className="text-center py-6" data-testid="plaid-connect-area">
                      <div className="w-16 h-16 bg-[#502D3C]/5 rounded-2xl flex items-center justify-center mx-auto mb-3">
                        <Landmark className="h-8 w-8 text-[#502D3C]" />
                      </div>
                      <h3 className="text-sm font-semibold text-[#502D3C]">Connect with Plaid</h3>
                      <p className="text-xs text-[#64748B] mt-1 mb-4 max-w-md mx-auto">Securely link your bank account. We request read-only access to account balances and 90-day transaction history for underwriting.</p>
                      <Button variant="outline" className="text-[#502D3C] border-[#502D3C]/30 hover:bg-[#502D3C]/5" onClick={simulatePlaid} disabled={plaidConnecting} data-testid="plaid-connect-btn">
                        {plaidConnecting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Connecting...</> : <><Landmark className="h-4 w-4 mr-2" />Connect Bank Account</>}
                      </Button>
                      {plaidConnecting && (
                        <div className="mt-4 space-y-2" data-testid="plaid-sim-steps">
                          <SimStep label="Initializing secure connection..." done />
                          <SimStep label="Authenticating with bank..." done={false} />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div data-testid="bank-connected">
                      <div className="flex items-center gap-2 mb-4">
                        <Check className="h-5 w-5 text-green-600" />
                        <span className="text-sm font-medium text-green-600">Bank account connected</span>
                        {isHris && <VerifiedBadge />}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <FormField label="Bank Name" required verified={isHris}>
                          <Input value={s2a.bank_name} readOnly className="h-9 text-sm bg-gray-50" />
                        </FormField>
                        <FormField label="Account Type" required verified={isHris}>
                          <Input value={s2a.account_type} readOnly className="h-9 text-sm bg-gray-50" />
                        </FormField>
                        <FormField label="Routing Number" required verified={isHris}>
                          <Input value={s2a.routing_number} readOnly className="h-9 text-sm bg-gray-50 font-mono" />
                        </FormField>
                        <FormField label="Account (last 4)" required verified={isHris}>
                          <Input value={`****${s2a.account_last4}`} readOnly className="h-9 text-sm bg-gray-50 font-mono" />
                        </FormField>
                      </div>
                      {bankMetrics && (
                        <div className="mt-4 grid grid-cols-3 gap-3" data-testid="bank-metrics">
                          <MetricCard label="Avg Daily Balance" value={`$${(bankMetrics.avg_daily_balance / 1e6).toFixed(1)}M`} />
                          <MetricCard label="Monthly Burn Rate" value={`$${(bankMetrics.monthly_burn_rate / 1e3).toFixed(0)}K`} />
                          <MetricCard label="Months of Runway" value={`${bankMetrics.months_of_runway.toFixed(1)}`} />
                          <MetricCard label="Min Balance (90d)" value={`$${(bankMetrics.min_balance_90d / 1e6).toFixed(1)}M`} />
                          <MetricCard label="Cash Flow Volatility" value={`${(bankMetrics.cash_flow_volatility * 100).toFixed(1)}%`} />
                          <MetricCard label="NSF Events (90d)" value={bankMetrics.nsf_count_90d} status={bankMetrics.nsf_count_90d > 0 ? "risk" : "good"} />
                        </div>
                      )}
                    </div>
                  )}
                  <WhyBox>Bank data serves dual purposes: (1) set up ACH auto-debit for card payments, and (2) provide cash balance data for underwriting. Average daily balance is the primary input to the credit limit formula. Monthly burn rate calculates runway, which directly feeds the PD model.</WhyBox>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#502D3C]">2B. Revenue & Business Metrics</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Annual Revenue (TTM)" required hint="Approximate is OK">
                      <Input value={s2b.annual_revenue} onChange={e => setS2b(p => ({ ...p, annual_revenue: e.target.value }))} placeholder="e.g. 5000000" type="number" className="h-9 text-sm" data-testid="field-revenue" />
                    </FormField>
                    <FormField label="Monthly Recurring Revenue (MRR)" hint="For SaaS / subscription businesses">
                      <Input value={s2b.mrr} onChange={e => setS2b(p => ({ ...p, mrr: e.target.value }))} placeholder="e.g. 400000" type="number" className="h-9 text-sm" data-testid="field-mrr" />
                    </FormField>
                  </div>
                  <FormField label="Revenue Model" required>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {refData.revenue_models.map(m => (
                        <label key={m} className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs cursor-pointer transition-colors", s2b.revenue_model?.includes?.(m) ? "bg-[#502D3C] text-white border-[#502D3C]" : "bg-white text-[#64748B] border-gray-200 hover:border-[#502D3C]/30")} data-testid={`rev-model-${m}`}>
                          <input type="checkbox" className="hidden" checked={s2b.revenue_model?.includes?.(m) || false} onChange={e => {
                            const arr = s2b.revenue_model || [];
                            setS2b(p => ({ ...p, revenue_model: e.target.checked ? [...arr, m] : arr.filter(x => x !== m) }));
                          }} />
                          {m}
                        </label>
                      ))}
                    </div>
                  </FormField>
                  <div className="grid grid-cols-3 gap-4">
                    <FormField label="Profitable?" required>
                      <Select value={s2b.profitable} onValueChange={v => setS2b(p => ({ ...p, profitable: v }))}>
                        <SelectTrigger className="h-9 text-sm" data-testid="field-profitable"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem><SelectItem value="not_yet">Not yet</SelectItem></SelectContent>
                      </Select>
                    </FormField>
                    <FormField label="Number of Employees" required verified={isHris && !!s2b.employee_count}>
                      <Input value={s2b.employee_count} onChange={e => setS2b(p => ({ ...p, employee_count: e.target.value }))} type="number" className="h-9 text-sm" data-testid="field-employees" />
                    </FormField>
                    <FormField label="Monthly Payroll (approx)" verified={isHris && !!s2b.monthly_payroll}>
                      <Input value={s2b.monthly_payroll} onChange={e => setS2b(p => ({ ...p, monthly_payroll: e.target.value }))} type="number" placeholder="e.g. 520000" className="h-9 text-sm" data-testid="field-payroll" />
                    </FormField>
                  </div>
                  {isHris && <div className="bg-green-50 border border-green-100 rounded-lg p-2.5"><p className="text-[11px] text-green-700"><ShieldCheck className="h-3.5 w-3.5 inline mr-1" />Employee count and payroll data verified from your Rippling HRIS. This lets us validate bank-derived burn rate with high confidence — no other card issuer has this signal.</p></div>}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#502D3C]">2C. Funding History & Capital Structure</CardTitle>
                  <p className="text-[10px] text-[#64748B]">We verify funding via SEC EDGAR and Crunchbase automatically</p></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Total Funding Raised to Date" required>
                      <Input value={s2c.total_funding_raised} onChange={e => setS2c(p => ({ ...p, total_funding_raised: e.target.value }))} placeholder="e.g. 29000000" type="number" className="h-9 text-sm" data-testid="field-total-funding" />
                    </FormField>
                    <FormField label="Latest Round Stage" required>
                      <Select value={s2c.latest_round_stage} onValueChange={v => setS2c(p => ({ ...p, latest_round_stage: v }))}>
                        <SelectTrigger className="h-9 text-sm" data-testid="field-round-stage"><SelectValue placeholder="Select stage" /></SelectTrigger>
                        <SelectContent>{refData.round_stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </FormField>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Latest Round Amount" required>
                      <Input value={s2c.latest_round_amount} onChange={e => setS2c(p => ({ ...p, latest_round_amount: e.target.value }))} placeholder="e.g. 15000000" type="number" className="h-9 text-sm" data-testid="field-round-amount" />
                    </FormField>
                    <FormField label="Latest Round Date" required>
                      <Input type="date" value={s2c.latest_round_date} onChange={e => setS2c(p => ({ ...p, latest_round_date: e.target.value }))} className="h-9 text-sm" data-testid="field-round-date" />
                    </FormField>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Lead Investor(s)" required>
                      <Input value={s2c.lead_investors} onChange={e => setS2c(p => ({ ...p, lead_investors: e.target.value }))} placeholder="e.g. Lightspeed Venture Partners" className="h-9 text-sm" data-testid="field-lead-investors" />
                    </FormField>
                    <FormField label="Other Notable Investors">
                      <Input value={s2c.other_investors} onChange={e => setS2c(p => ({ ...p, other_investors: e.target.value }))} placeholder="e.g. General Catalyst, YC" className="h-9 text-sm" data-testid="field-other-investors" />
                    </FormField>
                  </div>
                  <FormField label="Post-Money Valuation" hint="Optional — helps assess fundraising optionality">
                    <Input value={s2c.post_money_valuation} onChange={e => setS2c(p => ({ ...p, post_money_valuation: e.target.value }))} placeholder="e.g. 100000000" type="number" className="h-9 text-sm" data-testid="field-valuation" />
                  </FormField>
                  <WhyBox>Funding data is verified automatically: we search SEC EDGAR for Form D filings by your legal name, and cross-reference with Crunchbase. Total raised vs. burn rate determines runway — a primary PD input. Tier 1 VC investors lower PD by 15-25% because they provide bridge financing in distress.</WhyBox>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#502D3C]">2D. Existing Debt & Credit Facilities</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <FormField label="Existing credit cards / lines of credit" required hint="Provider + limit (e.g. Amex $200K)">
                    <Input value={s2d.existing_credit} onChange={e => setS2d(p => ({ ...p, existing_credit: e.target.value }))} placeholder="e.g. Amex Business $200K, SVB line $500K" className="h-9 text-sm" data-testid="field-existing-credit" />
                  </FormField>
                  <FormField label="Outstanding loans (venture debt, term loans)" required hint="Provider + amount + terms">
                    <Input value={s2d.outstanding_loans} onChange={e => setS2d(p => ({ ...p, outstanding_loans: e.target.value }))} placeholder="e.g. SVB venture debt $2M, 36-month term" className="h-9 text-sm" data-testid="field-outstanding-loans" />
                  </FormField>
                  <FormField label="Defaults, bankruptcies, or judgments (past 7 years)" required>
                    <Select value={s2d.defaults_history} onValueChange={v => setS2d(p => ({ ...p, defaults_history: v }))}>
                      <SelectTrigger className="h-9 text-sm" data-testid="field-defaults"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="yes">Yes (provide details)</SelectItem></SelectContent>
                    </Select>
                  </FormField>
                </CardContent>
              </Card>
            </div>
          )}

          {/* === STEP 3: Credit Bureau === */}
          {step === 3 && (
            <div className="space-y-6" data-testid="step-3-content">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#502D3C]">Business Credit Bureau Authorization</CardTitle>
                  <p className="text-[10px] text-[#64748B]">Authorize pulls from D&B, Experian Business, and Equifax Business</p></CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                    <p className="text-xs text-blue-700 leading-relaxed">We only pull <strong>business</strong> credit reports using your EIN. This does not affect any individual's personal credit score. Many startups have "thin" business credit files — this is completely normal and is NOT a negative signal. Our model automatically reweights to rely more on cash reserves and investor backing.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="D-U-N-S Number" hint="Optional — we can look it up by EIN">
                      <Input value={s3.duns_number} onChange={e => setS3(p => ({ ...p, duns_number: e.target.value }))} placeholder="9-digit number" className="h-9 text-sm font-mono" data-testid="field-duns" />
                    </FormField>
                    <FormField label="Experian BIN" hint="Optional — we look it up by EIN">
                      <Input value={s3.experian_bin} onChange={e => setS3(p => ({ ...p, experian_bin: e.target.value }))} placeholder="Experian business ID" className="h-9 text-sm font-mono" data-testid="field-experian" />
                    </FormField>
                  </div>

                  <Separator />

                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <h4 className="text-xs font-semibold text-[#502D3C]">What the bureau reports tell us:</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-white rounded p-3 border border-gray-100">
                        <p className="text-[10px] font-semibold text-[#502D3C]">D&B (PAYDEX)</p>
                        <p className="text-[10px] text-[#64748B] mt-1">Score 1-100. Measures how promptly you pay vendors. PAYDEX 80+ = on time.</p>
                      </div>
                      <div className="bg-white rounded p-3 border border-gray-100">
                        <p className="text-[10px] font-semibold text-[#502D3C]">Experian Intelliscore</p>
                        <p className="text-[10px] text-[#64748B] mt-1">Composite risk score 1-100. Feeds directly into PD calculation.</p>
                      </div>
                      <div className="bg-white rounded p-3 border border-gray-100">
                        <p className="text-[10px] font-semibold text-[#502D3C]">Equifax Business</p>
                        <p className="text-[10px] text-[#64748B] mt-1">Payment Index + derogatory filings. Supplements D&B and Experian.</p>
                      </div>
                    </div>
                    <div className="bg-amber-50 border border-amber-100 rounded p-2.5">
                      <p className="text-[10px] text-amber-700"><strong>Thin file?</strong> Model auto-reweights: Bureau score drops from 25% to 5%, Cash position rises from 30% to 45%, Funding/investor quality rises from 15% to 25%.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-lg border border-gray-200">
                    <Checkbox checked={s3.bureau_consent} onCheckedChange={v => setS3(p => ({ ...p, bureau_consent: v }))} id="bureau-consent" data-testid="field-bureau-consent" />
                    <label htmlFor="bureau-consent" className="text-xs text-[#502D3C] leading-relaxed cursor-pointer">
                      I authorize Rippling and Celtic Bank to obtain business credit reports from Dun & Bradstreet, Experian Business, and Equifax Business using the company's EIN provided in this application.
                    </label>
                  </div>
                  <WhyBox>Business credit bureau data provides payment history signals. D&B PAYDEX 80+ means you pay on time, lowering PD. For startups with thin files, the model shifts weight to cash + funding — you won't be penalized for being young.</WhyBox>
                </CardContent>
              </Card>
            </div>
          )}

          {/* === STEP 4: Card Setup === */}
          {step === 4 && (
            <div className="space-y-6" data-testid="step-4-content">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#502D3C]">Credit Request & Card Program Setup</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <FormField label="Estimated Monthly Card Spend" required>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {refData.spend_ranges.map(r => (
                        <button key={r} onClick={() => setS4(p => ({ ...p, estimated_monthly_spend: r }))} className={cn("px-4 py-2 rounded-lg border text-xs font-medium transition-colors", s4.estimated_monthly_spend === r ? "bg-[#502D3C] text-white border-[#502D3C]" : "bg-white text-[#64748B] border-gray-200 hover:border-[#502D3C]/30")} data-testid={`spend-${r}`}>{r}</button>
                      ))}
                    </div>
                  </FormField>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField label="Number of Cards Needed" required hint="Physical + virtual">
                      <Input value={s4.num_cards} onChange={e => setS4(p => ({ ...p, num_cards: e.target.value }))} type="number" placeholder="e.g. 15" className="h-9 text-sm" data-testid="field-num-cards" />
                    </FormField>
                    <FormField label="Preferred Billing Cycle" required>
                      <Select value={s4.billing_cycle} onValueChange={v => setS4(p => ({ ...p, billing_cycle: v }))}>
                        <SelectTrigger className="h-9 text-sm" data-testid="field-billing-cycle"><SelectValue placeholder="Select cycle" /></SelectTrigger>
                        <SelectContent>{refData.billing_cycles.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </FormField>
                  </div>
                  <FormField label="Primary Spend Categories" required>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {refData.spend_categories.map(c => (
                        <label key={c} className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs cursor-pointer transition-colors", s4.spend_categories?.includes?.(c) ? "bg-[#502D3C] text-white border-[#502D3C]" : "bg-white text-[#64748B] border-gray-200 hover:border-[#502D3C]/30")} data-testid={`cat-${c}`}>
                          <input type="checkbox" className="hidden" checked={s4.spend_categories?.includes?.(c) || false} onChange={e => {
                            const arr = s4.spend_categories || [];
                            setS4(p => ({ ...p, spend_categories: e.target.checked ? [...arr, c] : arr.filter(x => x !== c) }));
                          }} />
                          {c}
                        </label>
                      ))}
                    </div>
                  </FormField>
                  <FormField label="Requested Credit Limit" hint="Optional — we'll approve up to the model-calculated maximum regardless">
                    <Input value={s4.requested_limit} onChange={e => setS4(p => ({ ...p, requested_limit: e.target.value }))} type="number" placeholder="e.g. 200000" className="h-9 text-sm" data-testid="field-requested-limit" />
                  </FormField>
                  <Separator />
                  <p className="text-xs font-medium text-[#502D3C]">Card Admin Contact</p>
                  <div className="grid grid-cols-3 gap-4">
                    <FormField label="Name" required>
                      <Input value={s4.admin_name} onChange={e => setS4(p => ({ ...p, admin_name: e.target.value }))} placeholder="Full name" className="h-9 text-sm" data-testid="field-admin-name" />
                    </FormField>
                    <FormField label="Email" required>
                      <Input value={s4.admin_email} onChange={e => setS4(p => ({ ...p, admin_email: e.target.value }))} placeholder="email@company.com" type="email" className="h-9 text-sm" data-testid="field-admin-email" />
                    </FormField>
                    <FormField label="Phone" required>
                      <Input value={s4.admin_phone} onChange={e => setS4(p => ({ ...p, admin_phone: e.target.value }))} placeholder="(555) 123-4567" className="h-9 text-sm" data-testid="field-admin-phone" />
                    </FormField>
                  </div>
                  <WhyBox>Estimated spend sets the "expected utilization" for EAD modeling and sanity-checks the credit limit. Spend categories affect risk — travel-heavy spend is riskier than SaaS-heavy. Credit limit formula: min(Cash-based cap, Usage-based cap, Segment cap).</WhyBox>
                </CardContent>
              </Card>
            </div>
          )}

          {/* === STEP 5: Authorization === */}
          {step === 5 && (
            <div className="space-y-6" data-testid="step-5-content">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#502D3C]">Authorization & E-Signature</CardTitle>
                  <p className="text-[10px] text-[#64748B]">Review and provide legal consent</p></CardHeader>
                <CardContent className="space-y-4">
                  {/* Summary review */}
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <h4 className="text-xs font-semibold text-[#502D3C]">Application Summary</h4>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                      <SummaryRow label="Company" value={s1a.legal_name} />
                      <SummaryRow label="EIN" value={s1a.ein} />
                      <SummaryRow label="Entity" value={`${s1a.entity_type} — ${s1a.state_of_incorporation}`} />
                      <SummaryRow label="Control Person" value={s1b.full_name} />
                      <SummaryRow label="Bank" value={s2a.bank_name || "Not connected"} />
                      <SummaryRow label="Annual Revenue" value={s2b.annual_revenue ? `$${Number(s2b.annual_revenue).toLocaleString()}` : "—"} />
                      <SummaryRow label="Total Funding" value={s2c.total_funding_raised ? `$${Number(s2c.total_funding_raised).toLocaleString()}` : "—"} />
                      <SummaryRow label="Latest Round" value={s2c.latest_round_stage || "—"} />
                      <SummaryRow label="Est. Monthly Spend" value={s4.estimated_monthly_spend || "—"} />
                      <SummaryRow label="Billing Cycle" value={s4.billing_cycle || "—"} />
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200">
                      <Checkbox checked={s5.cert_true && s5.cert_bureau && s5.cert_ach && s5.cert_agreement && s5.cert_officer && s5.cert_monitoring} onCheckedChange={v => setS5({ ...s5, cert_true: v, cert_bureau: v, cert_ach: v, cert_agreement: v, cert_officer: v, cert_monitoring: v })} data-testid="consent-select-all" />
                      <label className="text-xs font-medium text-[#502D3C] cursor-pointer">Select all</label>
                    </div>
                    <ConsentCheck checked={s5.cert_true} onChange={v => setS5(p => ({ ...p, cert_true: v }))} testId="consent-true">
                      I certify that all information provided is true and complete.
                    </ConsentCheck>
                    <ConsentCheck checked={s5.cert_bureau} onChange={v => setS5(p => ({ ...p, cert_bureau: v }))} testId="consent-bureau">
                      I authorize Celtic Bank and Rippling to obtain business credit reports from D&B, Experian Business, and Equifax Business.
                    </ConsentCheck>
                    <ConsentCheck checked={s5.cert_ach} onChange={v => setS5(p => ({ ...p, cert_ach: v }))} testId="consent-ach">
                      I authorize ACH debits from the connected bank account for card balance payments on each billing cycle due date.
                    </ConsentCheck>
                    <ConsentCheck checked={s5.cert_agreement} onChange={v => setS5(p => ({ ...p, cert_agreement: v }))} testId="consent-agreement">
                      I agree to the <a href="#" className="text-[#502D3C] underline">Celtic Bank Corporate Card Cardholder Agreement</a>.
                    </ConsentCheck>
                    <ConsentCheck checked={s5.cert_officer} onChange={v => setS5(p => ({ ...p, cert_officer: v }))} testId="consent-officer">
                      I confirm I am an authorized officer/representative with authority to bind the applicant company.
                    </ConsentCheck>
                    <ConsentCheck checked={s5.cert_monitoring} onChange={v => setS5(p => ({ ...p, cert_monitoring: v }))} testId="consent-monitoring">
                      I acknowledge that credit limits may be adjusted based on ongoing monitoring of account health, cash position, and payment behavior.
                    </ConsentCheck>
                  </div>

                  <Separator />

                  <FormField label="E-Signature (type your full legal name)" required>
                    <Input value={s5.signature_name} onChange={e => setS5(p => ({ ...p, signature_name: e.target.value }))} placeholder="Type your full legal name" className="h-10 text-sm font-serif italic" data-testid="field-signature" />
                    <p className="text-[9px] text-[#94A3B8] mt-1">By typing your name above, you agree that this constitutes your electronic signature under the E-SIGN Act.</p>
                  </FormField>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6 pb-8">
            <Button variant="outline" onClick={prevStep} disabled={step === 1} data-testid="prev-btn"><ChevronLeft className="h-4 w-4 mr-1" />Back</Button>
            <div className="flex items-center gap-2">
              {step < 5 ? (
                <Button variant="outline" className="text-[#502D3C] border-[#502D3C]/30 hover:bg-[#502D3C]/5" onClick={nextStep} data-testid="next-btn">Continue<ChevronRight className="h-4 w-4 ml-1" /></Button>
              ) : (
                <Button variant="outline" className="text-[#502D3C] border-[#502D3C]/30 hover:bg-[#502D3C]/5 px-8" onClick={handleSubmit} disabled={!s5.cert_true || !s5.cert_bureau || !s5.cert_ach || !s5.cert_agreement || !s5.cert_officer || !s5.cert_monitoring || !s5.signature_name} data-testid="submit-btn">
                  <FileSignature className="h-4 w-4 mr-2" />Submit Application
                </Button>
              )}
            </div>
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
}

// ===== HELPER COMPONENTS =====

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-[10px] text-[#64748B]">{label}</span>
      <span className="text-xs font-medium text-[#502D3C]">{value || "—"}</span>
    </div>
  );
}

function ConsentCheck({ checked, onChange, testId, children }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
      <Checkbox checked={checked} onCheckedChange={onChange} data-testid={testId} />
      <label className="text-xs text-[#502D3C] leading-relaxed cursor-pointer">{children}</label>
    </div>
  );
}

function MetricCard({ label, value, status }) {
  return (
    <div className={cn("p-2.5 rounded-lg border", status === "risk" ? "bg-red-50 border-red-100" : status === "good" ? "bg-green-50 border-green-100" : "bg-gray-50 border-gray-100")}>
      <p className="text-[9px] text-[#64748B] uppercase tracking-wider">{label}</p>
      <p className={cn("text-sm font-bold mt-0.5", status === "risk" ? "text-red-600" : "text-[#502D3C]")}>{value}</p>
    </div>
  );
}

function SimStep({ label, done }) {
  return (
    <div className="flex items-center gap-2 text-xs text-[#64748B]">
      {done ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Loader2 className="h-3.5 w-3.5 animate-spin text-[#502D3C]" />}
      {label}
    </div>
  );
}
